import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'

import { Cookie } from '../functions/cookie'
import {
  generateISO,
  getDomainFromURL,
  getVisitorId,
  mapTiming,
} from '../base/helpers'
import { create } from './endpoints'

import { CookieName, CookieSession, CookieTracking } from '../types'
import {
  AkivaData,
  AkivaDuration,
  AkivaGlobalSearch,
  AkivaPage,
  AkivaPerformance,
  AkivaProps,
  AkivaRequest,
  AkivaType,
  AkivaView,
} from './types'
import { path } from '@wap-client/core'
import { getMeasures } from './performance/getMeasures'
import { getNavigationTiming } from './performance/getNavigationTiming'
import { getPageLoadTime } from './performance/getPageLoadTime'

export const Akiva = async ({ app, type, referrer, data }: AkivaProps) => {
  if (app && type) {
    let tracking = Cookie.get<CookieTracking>(CookieName.Tracking)
    let user = Cookie.get<string>(CookieName.UserId)
    let session = Cookie.get<CookieSession>(CookieName.Session)

    let request: AkivaRequest = {
      type: type,
      page: {
        id: String(app.page.id),
        parameters: {},
      } as AkivaPage,
      client: {
        sessionId: session?.sessionId || '',
        userId: tracking?.userId || '',
        timing: JSON.stringify(mapTiming()),
      },
      trackingCode: process.env.NEXT_PUBLIC_AKIVA_KEY || '',
    }

    if (!tracking || !tracking.userId || !tracking.fp) {
      tracking = {
        userId: `${uuidv4()}-${Date.now()}`,
        pageId: String(app.page.id),
        referrer: referrer || '',
        fp: `${getVisitorId()}`,
      }
      request.client.userId = tracking.userId
    }

    if (!session || !session.sessionId) {
      session = {
        sessionId: `${uuidv4()}-${Date.now()}`,
        sessionStartAt: generateISO(),
      }
      request.client.sessionId = session.sessionId
      Cookie.set<CookieSession>(CookieName.Session, session)
    }

    if (!user) {
      Cookie.set<string>(CookieName.UserId, tracking.userId)
    }

    request.page.referrer = referrer || document?.referrer || document.URL
    request.page.parameters = {
      fp: tracking.fp,
      lang: app.language,
      path: app.page.path,
      sessionStartAt: session.sessionStartAt,
      userLocalAt: moment().format(),
    }

    switch (request.type) {
      case AkivaType.View:
      case AkivaType.Performance:
        {
          tracking.pageInitializedAt = generateISO()
          tracking.pageId = String(app.page.id)
          tracking.referrer = document.URL
          request.page = {
            ...request.page,
            parameters: {
              ...request.page.parameters,
              pageInitializedAt: generateISO(),
            },
          } as AkivaView
          if (request.type === AkivaType.Performance) {
            const measure = getMeasures(window, [
              getDomainFromURL(app.environment.url),
              getDomainFromURL(app.environment.cdn),
            ])
            const navigationTiming = getNavigationTiming(window)

            request.page = {
              ...request.page,
              requestedFiles: null,
              parameters: {
                ...request.page.parameters,
                ...navigationTiming,
                contentLoadAsMilliseconds: measure.contentLoadTime,
                totalPageLoadAsSeconds: getPageLoadTime(window),
              },
            } as unknown as AkivaPerformance
          }
        }
        break
      case AkivaType.Duration:
        {
          const pageInitializedAt = new Date(
            tracking.pageInitializedAt || Date(),
          )
          const pageExitAt = new Date(generateISO())
          const duration =
            Math.abs(pageExitAt.getTime() - pageInitializedAt.getTime()) / 1000

          request.page = {
            ...request.page,
            referrer: path.url(app.environment, app.page.path),
            parameters: {
              ...request.page.parameters,
              pageExitAt: generateISO(),
              pageInitializedAt: new Date(
                tracking.pageInitializedAt || Date(),
              ).toISOString(),
              durationAsSeconds: duration,
            },
          } as AkivaDuration
        }
        break
      case AkivaType.GlobalSearch:
        {
          const idata = data as AkivaGlobalSearch

          request = {
            ...request,
            page: {
              ...request.page,
              ...(idata as AkivaGlobalSearch),
              parameters: {
                ...request.page.parameters,
                ...idata.parameters,
                fullPath: `${path.url(app.environment, app.page.path)}`,
              },
            },
            client: {
              ...request.client,
              timing: JSON.stringify({}),
            },
          }
        }
        break
      default:
        {
          const idata = data as AkivaData

          request.page.parameters = {
            ...request.page.parameters,
            ...idata,
          }
        }
        break
    }

    Cookie.set<CookieTracking>(CookieName.Tracking, tracking)
    await create(request)
  }
}
